import React, { Fragment } from 'react';
import Title from '../Typography/Title/Title';
import Link from '../Link/Link';
import Icon, { IconName } from '../Icon/Icon';
import Div from '../Element/Div/Div';
import Flex from '../Flex/Flex';
import Gutter from '../Gutter/Gutter';
import BodyText from '../Typography/BodyText/BodyText';
import Divider from '../Divider/Divider';

import colors from '../../shared/colors.json';

import styles from './UserMenu.module.scss';
import { AdditionalLoginLinkShape } from './Login';

type UserMenuProps = {
  /** User's name */
  username: string;
  /** User's Login History Key*/
  userloginHistoryKey?: string;
  /** User's Login History */
  userloginHistory?: string;
  /** User's Login History Link*/
  userloginHistoryLink?: string;
  userMenuLinks?: UserMenuLink[];

  logoutButton: {
    /** Logout button label */
    logoutButtonLabel: string;
    /** Logout button onClick callback */
    onLogoutButtonClick: () => void;
  };
  /** Additional links on the bottom of the menu */
  additionalLinks?: AdditionalLoginLinkShape[];
  /** Additional links alignment */
  additionalLinkAlignLeft?: boolean | undefined;
};

export type UserMenuLink = {
  /** Name of the service user is logged into */
  label: string;
  /** Name of the icon */
  icon?: IconName;
  /** Link to service user is logged in */
  link: string;
  /** Unique key */
  key?: string;
  /** Open in new window */
  openInNewWindow?: boolean;
};

const UserMenu: React.FunctionComponent<UserMenuProps> = ({
  username,
  userloginHistoryKey,
  userloginHistory,
  userloginHistoryLink,
  userMenuLinks,
  logoutButton,
  additionalLinks,
  additionalLinkAlignLeft,
}) => {
  return (
    <Div className={styles.userMenu} data-testid="logged-in">
      <Title
        color="black"
        block={true}
        className={styles.userMenu_username}
        size="xxs"
        data-testid="logged-in-username"
      >
        {username}
      </Title>

      {userloginHistory && (
        <>
          <Gutter size="sm" />
          <Fragment>
            <Flex centeredVertically>
              <BodyText
                tagName="span"
                size="sm"
                className={styles.userMenu_userloginHistory}
              >
                {userloginHistoryKey}
                <Gutter size="xxs" style={{ minHeight: '2px' }} />
                <BodyText
                  tagName="span"
                  size="sm"
                  data-testid="logged-in-userloginHistory"
                >
                  {userloginHistoryLink ? (
                    <Link
                      block={true}
                      data-testid="logged-in-history-link"
                      to={userloginHistoryLink}
                    >
                      {userloginHistory}
                    </Link>
                  ) : (
                    userloginHistory
                  )}
                </BodyText>
              </BodyText>
            </Flex>
          </Fragment>
          <Divider color="gray300" width={100} />
        </>
      )}

      {userMenuLinks && (
        <>
          <Gutter size="sm" />

          {userMenuLinks.map(({ link, label, key, icon, openInNewWindow }) => (
            <Fragment key={key ? key : label}>
              <Link
                block={true}
                className={styles.userMenu_link}
                data-testid="logged-in-link"
                to={link}
                openInNewWindow={openInNewWindow}
              >
                <Flex centeredVertically>
                  {icon && (
                    <>
                      <Icon name={icon} color="inherit" size="sm" />
                      <Gutter size="xs" />
                    </>
                  )}

                  <BodyText tagName="span" size="sm">
                    {label}
                  </BodyText>
                </Flex>
              </Link>

              <Gutter size="sm" />
            </Fragment>
          ))}
        </>
      )}

      <Divider color="gray300" width={100} />

      <Flex
        backgroundHoverColor="gray200"
        className={styles.userMenu_logoutButton}
        data-testid="logged-in-logout-button"
        onClick={logoutButton.onLogoutButtonClick}
        centeredVertically
      >
        <svg viewBox="0 0 64 64" height="18" width="18">
          <path
            fill={colors.black.color}
            d="M57.41278,33.41534,45.41406,45.41406a1.99978,1.99978,0,0,1-2.82812-2.82812L51.17151,34H24a2,2,0,0,1,0-4H51.17151l-8.58557-8.58594a1.99978,1.99978,0,0,1,2.82812-2.82812L57.41278,30.58466a2.00855,2.00855,0,0,1,.25073.30664c.03174.04748.05335.099.08057.14843a1.17162,1.17162,0,0,1,.165.39539c.01715.05786.03949.11334.05139.17334a2.00573,2.00573,0,0,1,0,.78308c-.0119.06-.03424.11548-.05139.17334a1.17162,1.17162,0,0,1-.165.39539c-.02722.04943-.04883.10095-.08057.14843A2.00855,2.00855,0,0,1,57.41278,33.41534ZM34,52H16a4.00427,4.00427,0,0,1-4-4V16a4.00427,4.00427,0,0,1,4-4H34a2,2,0,0,0,0-4H16a8.00917,8.00917,0,0,0-8,8V48a8.00917,8.00917,0,0,0,8,8H34a2,2,0,0,0,0-4Z"
          />
        </svg>

        <Gutter size="xs" />

        <BodyText tagName="span">{logoutButton.logoutButtonLabel}</BodyText>
      </Flex>

      {additionalLinks && (
        <>
          {additionalLinks.map(({ label, link, icon, key }) => {
            return (
              <Fragment key={key ? key : label}>
                <Divider color="gray300" />

                <Link
                  color="gray800"
                  hoverColor="gray800"
                  to={link}
                  data-testid="additional-link"
                >
                  <Flex
                    backgroundColor="gray100"
                    backgroundHoverColor="gray300"
                    className={styles.userMenu_additionalLinkContent}
                    centeredVertically
                  >
                    {additionalLinkAlignLeft && (
                      <BodyText tagName="span">{label}</BodyText>
                    )}
                    {icon && (
                      <>
                        {additionalLinkAlignLeft && (
                          <Gutter size="xs" flexItem />
                        )}
                        <Icon name={icon} size="sm" color="gray500" />
                        {!additionalLinkAlignLeft && <Gutter size="xs" />}
                      </>
                    )}
                    {!additionalLinkAlignLeft && (
                      <BodyText tagName="span">{label}</BodyText>
                    )}
                  </Flex>
                </Link>
              </Fragment>
            );
          })}
        </>
      )}
    </Div>
  );
};

export default UserMenu;
